import React, { useState, useEffect } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { BiX, BiMenu } from "react-icons/bi";
import './navbar.css';
import logoImg from '../../assets/Logo.png';

// Section IDs to observe for active state
const sections = ['home', 'about', 'capabilities', 'infrastructure', 'products', 'faq', 'contact'];

const Navbar = () => {
  const [isOpen, setIsOpen] = useState(false);
  const [activeSection, setActiveSection] = useState('home');
  const [showNavbar, setShowNavbar] = useState(true); // Track navbar visibility
  const [lastScrollY, setLastScrollY] = useState(0); // Track last scroll position
  const location = useLocation();
  const navigate = useNavigate();

  // Scroll Observer to track active section
  useEffect(() => {
    // 1. Handle specific pages
    if (location.pathname === '/about-full') {
      setActiveSection('about');
      return;
    }
    if (location.pathname === '/product-full') {
      setActiveSection('products');
      return;
    }
    if (location.pathname === '/contact') {
      setActiveSection('contact');
      return;
    }
    if (location.pathname === '/capabilities') {
      setActiveSection('capabilities');
      return;
    }
    if (location.pathname === '/infrastructure') {
      setActiveSection('infrastructure');
      return;
    }
    if (location.pathname === '/faq') {
      setActiveSection('faq');
      return;
    }

    // 2. Clear active state if not on Home
    if (location.pathname !== '/') {
      setActiveSection('');
      return;
    }

    const observerOptions = {
      root: null,
      rootMargin: '-30% 0px -30% 0px', // Highlights when section is in middle of screen
      threshold: 0
    };

    const observerCallback = (entries) => {
      entries.forEach((entry) => {
        if (entry.isIntersecting) {
          setActiveSection(entry.target.id);
        }
      });
    };

    const observer = new IntersectionObserver(observerCallback, observerOptions);

    sections.forEach((sectionId) => {
      const element = document.getElementById(sectionId);
      if (element) observer.observe(element);
    });

    // Fallback for top of page
    const handleScrollTop = () => {
      if (window.scrollY < 100 && location.pathname === '/') {
        setActiveSection('home');
      }
    };

    // Scroll Handler for Navbar Visibility
    const controlNavbar = () => {
      if (typeof window !== 'undefined') {
        if (window.scrollY > lastScrollY && window.scrollY > 100) {
          // If scrolling down AND passed 100px, hide navbar
          setShowNavbar(false);
        } else {
          // If scrolling up, show navbar
          setShowNavbar(true);
        }
        // Remember current page location to use in the next move
        setLastScrollY(window.scrollY);
      }
    };

    window.addEventListener('scroll', handleScrollTop);
    window.addEventListener('scroll', controlNavbar);

    return () => {
      observer.disconnect();
      window.removeEventListener('scroll', handleScrollTop);
      window.removeEventListener('scroll', controlNavbar);
    };
  }, [location.pathname, lastScrollY]); // Added lastScrollY dependency

  // 2. The Smart Navigation Function
  const handleNavigation = (sectionId) => {
    setIsOpen(false);

    // Special handling for Contact page
    if (sectionId === 'contact') {
      navigate('/contact');
      return;
    }

    // Special handling for About page
    if (sectionId === 'about') {
      navigate('/about-full');
      return;
    }

    // Special handling for Products page
    if (sectionId === 'products') {
      navigate('/product-full');
      return;
    }

    // Special handling for Capabilities page
    if (sectionId === 'capabilities') {
      navigate('/capabilities');
      return;
    }

    // Special handling for Infrastructure page
    if (sectionId === 'infrastructure') {
      navigate('/infrastructure');
      return;
    }

    // Special handling for FAQ (Navigates to /faq page)
    if (sectionId === 'faq') {
      navigate('/faq');
      return;
    }

    if (location.pathname === '/') {
      const element = document.getElementById(sectionId);
      if (element) {
        element.scrollIntoView({ behavior: 'smooth' });
      }
    } else {
      navigate('/', { state: { scrollTo: sectionId } });
    }
  };

  return (
    <nav className={`navbar navbar-expand-lg bg-white fixed-top py-3 shadow-sm w-100 ${showNavbar ? 'navbar-visible' : 'navbar-hidden'}`}>
      <div className="container-fluid px-3 px-lg-5">

        {/* Logo Section */}
        <div
          className="navbar-brand d-flex align-items-center cursor-pointer"
          onClick={() => handleNavigation('home')}
        >
          <img src={logoImg} alt="Logo" className="logo-img" />
        </div>

        {/* Mobile Toggle */}
        <button
          className="navbar-toggler border-0 shadow-none"
          type="button"
          onClick={() => setIsOpen(!isOpen)}
        >
          {isOpen ? <BiX size={30} /> : <BiMenu size={30} />}
        </button>

        {/* Nav Items */}
        <div className={`collapse navbar-collapse ${isOpen ? 'show' : ''}`}>
          <ul className="navbar-nav ms-auto mb-3 mb-lg-0 fw-medium">
            {sections.map((sectionId) => {
              const label = sectionId === 'home' ? 'Home' :
                sectionId === 'about' ? 'About Us' :
                  sectionId === 'faq' ? 'FAQ' :
                    sectionId.charAt(0).toUpperCase() + sectionId.slice(1);

              return (
                <li className="nav-item" key={sectionId}>
                  <button
                    className={`nav-link btn btn-link text-decoration-none ${activeSection === sectionId ? 'active' : 'text-dark'
                      }`}
                    onClick={() => handleNavigation(sectionId)}
                    data-text={label}
                  >
                    {label}
                  </button>
                </li>
              );
            })}
          </ul>


        </div>
      </div>
    </nav>
  );
};

export default Navbar;